import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HardDrive, RefreshCw, CheckCircle2, AlertTriangle, Loader2, CloudUpload } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminStorage() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [starting, setStarting] = useState(false);
    const pollRef = useRef(null);

    const fetchStatus = async () => {
        try {
            const r = await api.get("/admin/storage-migration/status");
            setStatus(r.data);
            return r.data;
        } catch (e) {
            return null;
        }
    };

    useEffect(() => {
        fetchStatus();
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const startPolling = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            const s = await fetchStatus();
            if (s && s.status !== "running") {
                clearInterval(pollRef.current);
                pollRef.current = null;
                if (s.status === "done") toast.success("Migración completada");
                if (s.status === "error") toast.error("La migración terminó con error");
            }
        }, 2500);
    };

    const handleStart = async () => {
        if (!window.confirm("¿Iniciar la migración de archivos a S3? Es segura e idempotente (no duplica ni borra nada).")) return;
        setStarting(true);
        try {
            await api.post("/admin/storage-migration/start");
            toast.success("Migración iniciada en segundo plano");
            await fetchStatus();
            startPolling();
        } catch (e) {
            const msg = e?.response?.data?.detail || "No se pudo iniciar la migración";
            toast.error(msg);
        } finally {
            setStarting(false);
        }
    };

    const handleRefresh = async () => {
        setLoading(true);
        await fetchStatus();
        setLoading(false);
    };

    const running = status?.status === "running";
    const st = status?.storage || {};
    const up = status?.uploads || {};

    const StatusBadge = () => {
        if (!status) return null;
        const map = {
            idle: { label: "Sin ejecutar", cls: "bg-gray-100 text-gray-700", icon: HardDrive },
            running: { label: "En progreso", cls: "bg-blue-100 text-blue-700", icon: Loader2 },
            done: { label: "Completada", cls: "bg-green-100 text-green-700", icon: CheckCircle2 },
            error: { label: "Con error", cls: "bg-red-100 text-red-700", icon: AlertTriangle },
        };
        const m = map[status.status] || map.idle;
        const Icon = m.icon;
        return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${m.cls}`} data-testid="migration-status-badge">
                <Icon className={`w-3.5 h-3.5 ${status.status === "running" ? "animate-spin" : ""}`} />
                {m.label}
            </span>
        );
    };

    return (
        <div className="max-w-3xl space-y-6" data-testid="admin-storage-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <CloudUpload className="w-6 h-6 text-[hsl(var(--accent))]" />
                        Almacenamiento (AWS S3)
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Migra a tu bucket S3 todos los archivos existentes referenciados en la base de datos.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading} data-testid="migration-refresh-button">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualizar
                </Button>
            </div>

            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                        <span>Estado de la migración</span>
                        <StatusBadge />
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">Bucket:</span>
                        <span className="font-mono font-medium" data-testid="migration-bucket">{status?.bucket || "—"}</span>
                        {status?.s3_enabled
                            ? <span className="text-green-600 text-xs font-semibold">S3 activo</span>
                            : <span className="text-red-600 text-xs font-semibold">S3 no configurado</span>}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-lg border p-3">
                            <div className="text-xs font-semibold text-muted-foreground mb-2">Fase 1 — Object Storage → S3</div>
                            <div className="grid grid-cols-2 gap-y-1 text-sm">
                                <span className="text-muted-foreground">Total</span><span className="text-right font-medium" data-testid="stat-storage-total">{st.total ?? 0}</span>
                                <span className="text-muted-foreground">Copiados</span><span className="text-right font-medium text-green-600">{st.copied ?? 0}</span>
                                <span className="text-muted-foreground">Ya en S3</span><span className="text-right font-medium">{st.skipped ?? 0}</span>
                                <span className="text-muted-foreground">Fallidos</span><span className="text-right font-medium text-red-600">{st.failed ?? 0}</span>
                            </div>
                        </div>
                        <div className="rounded-lg border p-3">
                            <div className="text-xs font-semibold text-muted-foreground mb-2">Fase 2 — Archivos locales → S3</div>
                            <div className="grid grid-cols-2 gap-y-1 text-sm">
                                <span className="text-muted-foreground">Migrados</span><span className="text-right font-medium text-green-600" data-testid="stat-uploads-migrated">{up.migrated ?? 0}</span>
                                <span className="text-muted-foreground">Docs actualizados</span><span className="text-right font-medium">{up.docs_updated ?? 0}</span>
                                <span className="text-muted-foreground">Perdidos</span><span className="text-right font-medium text-amber-600">{up.missing ?? 0}</span>
                            </div>
                        </div>
                    </div>

                    {status?.message && (
                        <div className="text-sm text-muted-foreground">{status.message}</div>
                    )}

                    {Array.isArray(status?.errors) && status.errors.length > 0 && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 max-h-40 overflow-auto" data-testid="migration-errors">
                            <div className="font-semibold mb-1">Errores ({status.errors.length}):</div>
                            {status.errors.slice(0, 20).map((e, i) => <div key={i}>• {e}</div>)}
                        </div>
                    )}

                    <Button
                        onClick={handleStart}
                        disabled={starting || running || !status?.s3_enabled}
                        className="w-full"
                        data-testid="start-migration-button"
                    >
                        {running
                            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Migración en progreso…</>
                            : <><CloudUpload className="w-4 h-4 mr-2" /> Iniciar migración a S3</>}
                    </Button>

                    <p className="text-xs text-muted-foreground">
                        La migración es <strong>segura e idempotente</strong>: no duplica archivos, no borra nada y conserva la copia
                        en Emergent Object Store como respaldo. Puedes cerrar esta página; la migración sigue en el servidor.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
